import { useState } from 'react';
import { FiMinus, FiPlus, FiShoppingCart } from 'react-icons/fi';
import { MdInventory } from 'react-icons/md';

import { useCart } from '@/context/cart';
import { useCartMode } from '@/hooks/useCartMode';
import { Button } from '@hubmaster/ui/Button';
import { toast } from 'sonner';

import { QuantityInput } from './QuantityInput';
import {
	Drawer,
	DrawerContent,
	DrawerDescription,
	DrawerHeader,
	DrawerTitle,
	DrawerTrigger,
} from './ui/drawer';

interface QuantityModalProps {
	product: any;
}

const fmt = (v: number) =>
	v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

export function QuantityModal({ product }: QuantityModalProps) {
	const [open, setOpen] = useState(false);
	const mode = useCartMode();
	const isWeight =
		product?.unit?.toUpperCase() === 'KG' ||
		product?.unit?.toUpperCase() === 'G';
	const decimals = isWeight ? 2 : 0;
	const minQuantity = isWeight ? 0.1 : 1;
	const [quantity, setQuantity] = useState(minQuantity);
	const productName = product?.name || '';
	const { addItem } = useCart();

	const coast: number | undefined = product?.coast;
	const stock: number | undefined = product?.stock;
	const price: number | undefined = product?.price;
	const margin =
		coast != null && coast > 0 && price != null
			? ((price - coast) / coast) * 100
			: null;

	const handleClose = () => {
		setQuantity(minQuantity);
		setOpen(false);
	};

	const increment = () =>
		setQuantity((prev) =>
			isWeight ? Number((prev + 0.1).toFixed(2)) : prev + 1,
		);

	const decrement = () =>
		setQuantity((prev) =>
			isWeight
				? Math.max(0.1, Number((prev - 0.1).toFixed(2)))
				: Math.max(1, prev - 1),
		);

	const handleAddToCart = () => {
		if (product?.id && price) {
			addItem(
				{
					id: product.id,
					barcode: product.barcode || '',
					name: product.name,
					price,
					coast,
					stock,
					unit: product.unit,
				},
				quantity,
			);
			toast.success(mode.toastAdded, {
				description: `${quantity} ${product?.unit ?? 'un'} de ${product.name}`,
			});
		}
		setQuantity(minQuantity);
		setOpen(false);
	};

	return (
		<Drawer open={open} onOpenChange={setOpen}>
			<DrawerTrigger asChild>
				<Button size="lg">
					<FiShoppingCart size={20} />
					{mode.addButton}
				</Button>
			</DrawerTrigger>
			<DrawerContent className="p-4">
				<DrawerHeader>
					<DrawerTitle>{mode.drawerTitle}</DrawerTitle>
					<DrawerDescription>{productName}</DrawerDescription>
				</DrawerHeader>

				{/* Painel de info — só no modo purchase_order */}
				{mode.isPurchaseOrder && (
					<div className="mb-4 grid grid-cols-3 divide-x rounded-lg border bg-gray-50 text-center text-sm">
						<div className="px-3 py-2">
							<p className="text-xs text-gray-500">Último custo</p>
							<p className="text-primary font-bold">
								{coast != null ? fmt(coast) : '—'}
							</p>
						</div>
						<div className="px-3 py-2">
							<p className="text-xs text-gray-500">Margem</p>
							<p
								className={`font-bold ${margin == null ? 'text-gray-400' : margin >= 0 ? 'text-green-600' : 'text-red-600'}`}
							>
								{margin != null ? `${margin.toFixed(1)}%` : '—'}
							</p>
						</div>
						<div className="px-3 py-2">
							<p className="text-xs text-gray-500">Estoque</p>
							<p className="flex items-center justify-center gap-1 font-bold">
								<MdInventory size={14} className="text-gray-400" />
								{stock != null ? `${stock} ${product?.unit ?? 'un'}` : '—'}
							</p>
						</div>
					</div>
				)}

				{/* Quantidade */}
				<div className="mb-4">
					<label className="mb-2 block text-sm font-medium text-gray-700">
						Quantidade {product?.unit ? `(${product.unit})` : ''}
					</label>
					<div className="flex items-center gap-3">
						<Button
							onClick={decrement}
							variant="outline"
							size="icon"
							className="size-10"
						>
							<FiMinus size={20} />
						</Button>
						<QuantityInput
							value={quantity}
							decimals={decimals}
							onChange={setQuantity}
							className="focus:border-primary focus:ring-primary h-10 flex-1 rounded-md border border-gray-300 text-center text-xl font-semibold focus:ring-2 focus:outline-none"
						/>
						<Button
							onClick={increment}
							variant="outline"
							size="icon"
							className="size-10"
						>
							<FiPlus size={20} />
						</Button>
					</div>
				</div>

				{/* Totais */}
				{quantity > 0 && price && (
					<div className="mb-6 rounded-lg border bg-gray-50 px-4 py-3">
						{mode.isPurchaseOrder && coast != null ? (
							<div className="flex w-full items-center space-y-1">
								<div className="flex-1 text-center">
									<p className="text-xs text-gray-500">Total Custo</p>
									<p className="text-primary text-xl font-bold">
										{fmt(coast * quantity)}
									</p>
								</div>
								<div className="flex-1 border-l text-center">
									<p className="text-xs text-gray-500">Total Venda</p>
									<p className="text-xl font-bold text-gray-700">
										{fmt(price * quantity)}
									</p>
								</div>
							</div>
						) : (
							<div className="text-center">
								<p className="text-sm text-gray-500">Total</p>
								<p className="text-primary text-2xl font-bold">
									{fmt(price * quantity)}
								</p>
							</div>
						)}
					</div>
				)}

				<div className="flex gap-3">
					<Button onClick={handleClose} variant="outline" className="flex-1">
						Cancelar
					</Button>
					<Button onClick={handleAddToCart} className="flex-1">
						Adicionar
					</Button>
				</div>
			</DrawerContent>
		</Drawer>
	);
}
